import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  IconLogo,
  TranslationProvider,
} from '@resy/ui-kitchen';
import RatingFormErrors from '../../components/RatingForm/RatingFormErrors';
import RatingFormThanks from '../../components/RatingForm/RatingFormThanks';
import ERROR_CODE from '../../components/RatingForm/RatingErrorCodes';
import Question from './Question';

import Spinner from '../../../images/icons/processing-spinner.svg';
import { getSurvey, postSurvey } from '../../../../../ui-kitchen/src/api/api';
import { surveyQuestionsTransform } from '../../../../../ui-kitchen/src/api/resyApiTransformer';
import './SurveyContainer.scss';

const SurveyContainer = ({
  loading,
}) => {
  const [displayLoading, setDisplayLoading] = useState(loading);
  const [surveyData, setSurveyData] = useState(null);
  const [surveyResponse, setSurveyResponse] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [sentSuccess, setSentSuccess] = useState(null);

  const surveyUrl = window.location.pathname.split('/').pop();

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const { data } = await getSurvey(surveyUrl);
        setSurveyData(data);
      } catch (e) {
        if (e.status === 400) {
          setErrorStatus(ERROR_CODE.alreadyRated);
        } else {
          setErrorStatus(ERROR_CODE.generic);
        }
      } finally {
        setDisplayLoading(false);
      }
    };
    if (!surveyData) { fetchSurvey(); }
  }, []);

  const onFormSubmit = async () => {
    const formattedSurveyResponse = Object.values(surveyResponse);
    try {
      await postSurvey(surveyUrl, { data: { responses: formattedSurveyResponse, source: 1 } });
      setSentSuccess(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setErrorStatus(ERROR_CODE.generic);
    }
  };

  const { reservation } = surveyData ?? {};
  const questions = surveyQuestionsTransform(surveyData);
  const venue = reservation?.venue?.name;
  const answerQuestion = (
    questionsetId,
    questionId,
    optionId,
    responseValue
  ) => {
    setSurveyResponse((prev) => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        questionset_id: questionsetId,
        option_id: optionId,
        response: responseValue || null,
      },
    }));
  };

  if (displayLoading) {
    return (
      <div className="RatingForm">
        <div className="RatingForm__head RatingForm__row">
          <IconLogo />
        </div>
        <Spinner
          className="Icon--loading"
          aria-hidden="true"
          width={33}
          height={32}
        />
      </div>
    );
  }

  if (errorStatus) {
    return (
      <RatingFormErrors errorStatus={errorStatus} />
    );
  }

  if (sentSuccess) {
    return <RatingFormThanks />;
  }

  return (
    <div className="RatingForm">
      <div className="RatingForm__head RatingForm__row">
        <IconLogo />
        <TranslationProvider
          translationKey="rate_reservation_header"
          translationValues={{ venueName: venue }}
        >
          <h1>
            {`How was ${venue}?`}
          </h1>
        </TranslationProvider>

      </div>
      <hr />

      <div className="RatingForm__row">
        <div className="SurveyContainer questionContainer">
          {questions.map((question, index) => (
            <div key={question.id} className="question">
              <Question
                questionIndex={index}
                question={question.question}
                type={question.type.id}
                options={question.options}
                questionId={question.id}
                response={surveyData?.responses}
                helpText={question.helpText}
                characterMinimum={question.characteMinimum}
                questionsetId={question.questionsetId}
                leftLabel={question.left_label}
                rightLabel={question.right_label}
                answerQuestion={answerQuestion}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="RatingForm__ctas RatingForm__row">
        <TranslationProvider translationKey="rate_reservation_submit">
          <Button
            lg
            onClick={onFormSubmit}
            data-test-id="rating_form-button-submit"
          >
            Submit Review
          </Button>
        </TranslationProvider>
      </div>
    </div>
  );
};

SurveyContainer.propTypes = {
  loading: PropTypes.bool,
};

SurveyContainer.defaultProps = {
  loading: true,
};

SurveyContainer.displayName = 'SurveyContainer';
export default SurveyContainer;
